import type {
  AgentComponent,
  AgentComponentBody,
  AgentComponentListQuery,
  AgentComponentPage,
  AgentComponentToolOption,
  AgentComponentType,
} from '@reach/shared-types/agent-components';
import { partnerApi, type ApiError, type ApiResponse } from '../../lib/api';

const BASE = '/partner/agent-components';

export const agentComponentKeys = {
  all: ['agent-components'] as const,
  lists: () => [...agentComponentKeys.all, 'list'] as const,
  list: (type: AgentComponentType) => [...agentComponentKeys.lists(), type] as const,
  count: (type: AgentComponentType) => [...agentComponentKeys.list(type), 'count'] as const,
  detail: (componentId: string) => [...agentComponentKeys.all, 'detail', componentId] as const,
  toolOptions: () => [...agentComponentKeys.all, 'tool-options'] as const,
} as const;

const isNotFoundError = (error: unknown): boolean => (error as ApiError).response?.status === 404;

/** Fetches one page of the partner's skills or guidance, alphabetically by name. */
export async function fetchAgentComponentPage(
  query: AgentComponentListQuery,
): Promise<AgentComponentPage> {
  const { type, limit, cursor } = query;
  const searchParams = new URLSearchParams({ type, limit: String(limit) });
  if (cursor) {
    searchParams.set('cursor', cursor);
  }
  const response = await partnerApi.request<ApiResponse<AgentComponentPage>>(`${BASE}?${searchParams}`);
  return response.data;
}

/** Fetches one saved skill or guidance; null when the partner has none with that id. */
export async function fetchAgentComponent(componentId: string): Promise<AgentComponent | null> {
  try {
    const response = await partnerApi.request<ApiResponse<AgentComponent>>(`${BASE}/${componentId}`);
    return response.data;
  } catch (error) {
    if (isNotFoundError(error)) {
      return null;
    }
    throw error;
  }
}

/** Fetches the partner's active tools, with how many components use each. */
export async function fetchAgentComponentToolOptions(): Promise<AgentComponentToolOption[]> {
  const response = await partnerApi.request<ApiResponse<AgentComponentToolOption[]>>(`${BASE}/tools`);
  return response.data;
}

/** Creates a skill or guidance. */
export async function createAgentComponent(body: AgentComponentBody): Promise<AgentComponent> {
  const response = await partnerApi.request<ApiResponse<AgentComponent>>(BASE, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return response.data;
}

/** Replaces a saved skill or guidance. */
export async function updateAgentComponent(
  componentId: string,
  body: AgentComponentBody,
): Promise<AgentComponent> {
  const response = await partnerApi.request<ApiResponse<AgentComponent>>(`${BASE}/${componentId}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  return response.data;
}

/** Deletes a skill or guidance. */
export async function deleteAgentComponent(componentId: string): Promise<void> {
  await partnerApi.request<void>(`${BASE}/${componentId}`, { method: 'DELETE' });
}
